import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Map as ImMap } from 'immutable';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { makeStyles } from 'tss-react/mui';

import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import SPayeeField from 'components/SPayeeField';
import CategoryField from 'components/QuickenControls/CategoryField';

import { getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import type { Category } from 'app-components/flux/categories/categoriesTypes';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { rootUiActions } from 'app-components/flux/root-ui';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { getLogger } from 'app-components/utils/core';

import { useAppDispatch, useAppSelector } from 'hooks/redux';

import useTxnActions from 'data/transactionActions/useTxnActions';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';

export const DIALOG_TYPE_REVIEW_CATEGORY = 'LLM_CATEGORIZE_TRANSACTIONS';
const log = getLogger('components/Dialogs/ReviewLlmCategoriesDialog');

const useStyles = makeStyles()((theme) => ({
  header: {
    marginBottom: theme.spacing(2),
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    maxHeight: '60vh',
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
  },
  transactionItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  invalidTransaction: {
    borderColor: theme.palette.error.main,
  },
  transactionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  amount: {
    fontWeight: 600,
  },
  helperText: {
    marginTop: theme.spacing(0.5),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  payeeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  editPayeeButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
}));

type ReviewResult = {
  success: boolean;
  updatedCount?: number;
  errors?: string[];
};

type TransactionItemProps = {
  transaction: TransactionRecord;
  isInvalidCategory: boolean;
  onCategoryChange: (txnId: string, newCoa: ChartOfAccountProps) => void;
  onPayeeChange: (txnId: string, newPayee: string) => void;
  categoriesById: ImMap<string, Category>;
  classes: ReturnType<typeof useStyles>['classes'];
  cx: ReturnType<typeof useStyles>['cx'];
};

const TransactionItem = ({
  transaction,
  isInvalidCategory,
  onCategoryChange,
  onPayeeChange,
  categoriesById,
  classes,
  cx,
}: TransactionItemProps) => {
  const txnId = transaction.id;

  const formatAmount = useFormatAmount();
  const [isEditingPayee, setIsEditingPayee] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);

  if (!txnId) {
    log.warn('Encountered a transaction without an id; skipping render.');
    return null;
  }

  const amount = transaction.amount ?? 0;
  const formattedAmount = formatAmount(Math.abs(amount), transaction.currency ?? 'USD', '0,0.00');
  const sign = amount < 0 ? '-' : '';
  const resolvedCategory = transaction.coa?.id ? categoriesById.get(transaction.coa.id) : null;
  const categoryDisplayName =
    resolvedCategory?.name ?? (resolvedCategory as Category | null | undefined)?.get?.('name') ?? 'Uncategorized';

  return (
    <Box className={cx(classes.transactionItem, isInvalidCategory && classes.invalidTransaction)}>
      <Box className={classes.transactionHeader}>
        <Box className={classes.payeeRow}>
          <Typography variant='bodyMedium'>{transaction.payee || 'No payee'}</Typography>
          <IconButton
            size='small'
            aria-label='Edit payee'
            className={classes.editPayeeButton}
            onClick={() => setIsEditingPayee(!isEditingPayee)}
          >
            <EditOutlinedIcon fontSize='small' />
          </IconButton>
        </Box>
        <Typography variant='paragraphRegular' className={classes.amount}>
          {`${sign}${formattedAmount}`}
        </Typography>
      </Box>

      {isEditingPayee && (
        <SPayeeField
          id={`review-llm-payee-${txnId}`}
          defaultValue={transaction.payee ?? ''}
          onChange={(newPayee: string) => {
            onPayeeChange(txnId, newPayee);
            setIsEditingPayee(false);
          }}
          fullWidth
          textFieldProps={{
            variant: 'filled',
            label: 'Payee',
            margin: 'dense',
          }}
        />
      )}

      {!isEditingCategory ? (
        <Box className={classes.payeeRow}>
          <Box>
            <Typography variant='bodyMedium'>{categoryDisplayName}</Typography>
            {isInvalidCategory && (
              <Typography variant='captionRegular' color='error' className={classes.helperText}>
                Please select a valid category
              </Typography>
            )}
          </Box>
          <IconButton
            size='small'
            aria-label='Edit category'
            className={classes.editPayeeButton}
            onClick={() => setIsEditingCategory(!isEditingCategory)}
          >
            <EditOutlinedIcon fontSize='small' />
          </IconButton>
        </Box>
      ) : (
        <CategoryField
          editable
          textFieldVariant='filled'
          label='Category'
          value={transaction.coa ?? undefined}
          onChange={(newCoa?: ChartOfAccountProps | null) => {
            if (newCoa) {
              onCategoryChange(txnId, newCoa);
              setIsEditingCategory(false);
            }
          }}
          margin='dense'
          helperText={isInvalidCategory ? 'Please select a valid category' : undefined}
          error={isInvalidCategory}
          width='100%'
        />
      )}
    </Box>
  );
};

type ReviewLlmCategoriesDialogProps = {
  transactions?: TransactionRecord[];
  callback?: (result: ReviewResult) => void;
  dialogId?: string;
};

const EMPTY_TRANSACTIONS: TransactionRecord[] = [];

const ReviewLlmCategoriesDialog = ({
  transactions = EMPTY_TRANSACTIONS,
  callback,
  dialogId,
}: ReviewLlmCategoriesDialogProps): JSX.Element => {
  const { classes, cx } = useStyles();
  const dispatch = useAppDispatch();
  const categoriesById = useAppSelector(getCategoriesById);
  const { saveTransactions } = useTxnActions();

  useEffect(() => {
    log.debug('ReviewLlmCategoriesDialog mounted with transactions:', transactions);
  }, [transactions]);

  const sanitizedTransactions = useMemo(
    () => transactions.filter((txn): txn is TransactionRecord & { id: string } => Boolean(txn?.id)),
    [transactions],
  );

  const initialMap = useMemo(
    () =>
      ImMap<string, TransactionRecord>(
        sanitizedTransactions.map((txn) => [txn.id as string, txn] as [string, TransactionRecord]),
      ),
    [sanitizedTransactions],
  );

  const [editedTransactions, setEditedTransactions] = useState(initialMap);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setEditedTransactions(initialMap);
  }, [initialMap]);

  const closeDialog = useCallback(() => {
    dispatch(rootUiActions.removeDialog(dialogId));
  }, [dispatch, dialogId]);

  const isCategoryValid = useCallback(
    (coa: ChartOfAccountProps | null | undefined) => {
      if (!coa) return false;
      if (coa.type === 'UNCATEGORIZED' || coa.type === 'NONE') return false;
      if (coa.type === 'CATEGORY') {
        return categoriesById.has(coa.id);
      }
      return true;
    },
    [categoriesById],
  );

  const handleCategoryChange = useCallback((txnId: string, newCoa: ChartOfAccountProps) => {
    setEditedTransactions((prev) => {
      const txn = prev.get(txnId);
      if (!txn) {
        log.warn('Transaction not found while updating category:', txnId);
        return prev;
      }
      return prev.set(txnId, txn.set('coa', newCoa));
    });
  }, []);
  const handlePayeeChange = useCallback((txnId: string, newPayee: string) => {
    setEditedTransactions((prev) => {
      const txn = prev.get(txnId);
      if (!txn) {
        log.warn('Transaction not found while updating payee:', txnId);
        return prev;
      }
      return prev.set(txnId, txn.set('payee', newPayee));
    });
  }, []);

  const transactionsToRender = useMemo(() => editedTransactions.valueSeq().toArray(), [editedTransactions]);
  const hasUncategorizedTransactions = useMemo(
    () => transactionsToRender.some((txn) => !isCategoryValid(txn.coa)),
    [transactionsToRender, isCategoryValid],
  );

  const sendCallback = useCallback(
    (result: ReviewResult) => {
      callback?.(result);
    },
    [callback],
  );

  const handleCancel = useCallback(() => {
    sendCallback({
      success: false,
      errors: ['User cancelled'],
    });
    closeDialog();
  }, [sendCallback, closeDialog]);

  const handleConfirm = useCallback(() => {
    const transactionsToSave = transactionsToRender;
    if (!transactionsToSave.length) {
      sendCallback({
        success: false,
        errors: ['No transactions available to update'],
      });
      return;
    }

    setIsSubmitting(true);

    const onResolve = (result?: unknown) => {
      const updatedCount = Array.isArray(result) ? result.length : transactionsToSave.length;
      sendCallback({
        success: true,
        updatedCount,
      });
      setIsSubmitting(false);
      closeDialog();
    };

    const onReject = (error?: unknown) => {
      log.error('Save transactions failed', error);
      sendCallback({
        success: false,
        errors: ['Failed to save transactions'],
      });
      setIsSubmitting(false);
      closeDialog();
    };

    saveTransactions(
      transactionsToSave,
      {
        callback: (optimisticResult: unknown) => {
          if (!optimisticResult) {
            setIsSubmitting(false);
          }
        },
      },
      onResolve,
      onReject,
    );
  }, [closeDialog, saveTransactions, sendCallback, transactionsToRender]);

  const disableConfirm = isSubmitting || hasUncategorizedTransactions || !transactionsToRender.length;

  return (
    <StdDialog
      onClose={handleCancel}
      open
      sharedcomponentid='review-llm-categories-dialog'
      showCloseButton
      size='md'
      title='Review Categories'
      paperProps={{
        sx: { overflowY: 'hidden' },
      }}
    >
      <StdDialogContent>
        <Box className={classes.header}>
          <Typography variant='paragraphRegular' color='text.secondary'>
            Review and confirm the AI-suggested categories for {sanitizedTransactions.length} transaction
            {sanitizedTransactions.length === 1 ? '' : 's'}.
          </Typography>
          {hasUncategorizedTransactions && (
            <Typography variant='captionRegular' color='error' className={classes.helperText}>
              Assign a valid category to each transaction before confirming.
            </Typography>
          )}
        </Box>

        {transactionsToRender.length ? (
          <Box className={classes.list}>
            {transactionsToRender.map((txn) => (
              <TransactionItem
                key={txn.id}
                transaction={txn}
                isInvalidCategory={!isCategoryValid(txn.coa)}
                onCategoryChange={handleCategoryChange}
                onPayeeChange={handlePayeeChange}
                categoriesById={categoriesById}
                classes={classes}
                cx={cx}
              />
            ))}
          </Box>
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant='paragraphRegular'>No transactions to review.</Typography>
          </Box>
        )}
      </StdDialogContent>

      <StdDialogActions
        divider
        primary={{
          id: 'review-llm-categories-confirm',
          children: 'Confirm',
          onClick: handleConfirm,
          disabled: disableConfirm,
          loading: isSubmitting,
        }}
        secondary={{
          id: 'review-llm-categories-cancel',
          children: 'Cancel',
          onClick: handleCancel,
          disabled: isSubmitting,
        }}
      />
    </StdDialog>
  );
};

export default ReviewLlmCategoriesDialog;
